import { AxiosError } from 'axios';
import { put, call, takeLatest, takeLeading, select, delay } from 'redux-saga/effects';
import get from 'lodash.get';

import { API, HardwareAPI } from '../api/base';
import {
  types,
  Status,
  Printer,
  PrintData,
  PrintJob,
  Scanner,
  ConfigurePrinterRequest,
  PingPrintersRequest,
  PingOnePrinterPayload,
  ScannerPingRequest,
  PaymentDevice,
  PaymentPingRequest,
  PaymentConfig,
  PaymentVoidRequest,
  LogToLocalServer,
  AuthorizePaymentResponse,
  HardwareResponse
} from './types';
import { selectors as hardwareSelectors } from './selectors';
import checkHealth from 'services/check-health';
import { selectors as cartSelectors, Cart } from 'services/cart';
import {
  actions as kioskActions,
  selectors as kioskSelectors,
  KioskHardware,
  UpdateTransactionsAsyncRequest
} from 'services/kiosk';
import { actions as eventActions, EventPayload, eventNames } from 'services/events';
import { actions as hardwareActions } from 'services/hardware';
import {
  CONNECTED,
  NOT_CONNECTED,
  OPERATIONAL,
  ERROR,
  USER_ERROR,
  OUT_OF_STOCK,
  UNKNOWN,
  NO_PING
} from 'constants/hardware';
import { types as maintenanceTypes } from 'services/maintenance';
import { getErrorMessage, getErrorCode } from 'services/api/normalizer';
import i18n from 'services/translation/i18n';
import { Configuration, selectors as configSelectors } from 'services/configuration';
import { actions as ticketingActions, selectors as ticketingSelectors } from 'services/ticketing';
import stripeInstance from 'services/payment/stripe';

function* getHardwareError(errCode: string, defaultMsg: string) {
  const config: Configuration = yield select(configSelectors.selectConfiguration);
  const ticketingPath: string = yield select(ticketingSelectors.selectTicketingConfigPath);
  const support = get(config, `${ticketingPath}.supportName.${i18n.language}`) || i18n.t('commonText.support');
  const hwErrMap = new Map();
  hwErrMap.set('paper_jam', i18n.t('hardwareError.paperJam'));
  hwErrMap.set('out_of_stock', i18n.t('hardwareError.outOfStock'));
  hwErrMap.set('printer_connect_error', i18n.t('hardwareError.printerError'));
  hwErrMap.set('printer_not_found', i18n.t('hardwareError.printerNotFound'));
  hwErrMap.set('printer_off', i18n.t('hardwareError.printerOff'));
  hwErrMap.set('cutter_jam', i18n.t('hardwareError.cutterJam'));
  hwErrMap.set('invalid_command', i18n.t('hardwareError.invalidCommand'));
  // hwErrMap.set('unknown', i18n.t('hardwareError.unknownCode'));
  hwErrMap.set('unknown', i18n.t('hardwareError.somethingWrong', { support }));
  hwErrMap.set('scanner_not_found', i18n.t('hardwareError.scannerNotFound'));
  hwErrMap.set('scanner_timeout', i18n.t('hardwareError.scannerTimeout'));
  hwErrMap.set('picture_failed', i18n.t('hardwareError.cameraFailed'));
  hwErrMap.set('camera_not_found', i18n.t('hardwareError.cameraNotFound'));
  hwErrMap.set('camera_timeout', i18n.t('hardwareError.cameraTimeout'));
  hwErrMap.set('not_connected', i18n.t('hardwareError.paymentTerminalNotConnected'));
  hwErrMap.set('terminal_error', i18n.t('hardwareError.paymentTerminalError'));
  hwErrMap.set('device_disconnected', i18n.t('hardwareError.paymentDeviceNotFound'));
  hwErrMap.set('device_error', i18n.t('hardwareError.paymentDeviceError'));
  hwErrMap.set('device_offline', i18n.t('hardwareError.paymentDeviceOffline'));
  hwErrMap.set('configuration_needed', i18n.t('hardwareError.paymentDeviceNeedConfig'));
  hwErrMap.set('configuration_failed', i18n.t('hardwareError.paymentDeviceConfigFailed'));
  hwErrMap.set('firmware_pending', i18n.t('hardwareError.paymentDeviceFirmwarePending'));
  hwErrMap.set('unsupported_card_type', i18n.t('hardwareError.unsupportedCard'));
  hwErrMap.set('invalid_card', i18n.t('hardwareError.invalidCard'));
  hwErrMap.set('swipe_failed', i18n.t('hardwareError.swipeFailed'));
  hwErrMap.set('card_declined', i18n.t('hardwareError.cardDeclined'));
  hwErrMap.set('pin_failed', i18n.t('hardwareError.invalidPin'));
  hwErrMap.set('card_already_inserted', i18n.t('hardwareError.cardInsertedEarly'));
  hwErrMap.set('card_removed_too_early', i18n.t('hardwareError.cardRemovedEarly'));
  hwErrMap.set('canceled', i18n.t('hardwareError.txnCancelled'));
  hwErrMap.set('timeout', i18n.t('hardwareError.txnTimeout'));
  hwErrMap.set('not_found', i18n.t('hardwareError.txnNotFound'));
  hwErrMap.set('failed', i18n.t('hardwareError.txnVoidFailed'));
  hwErrMap.set('payment_reboot_error', i18n.t('hardwareError.paymentDeviceRebootError'));
  hwErrMap.set('network_error', i18n.t('hardwareError.networkError'));

  const errMsg = hwErrMap.get(errCode) || defaultMsg;

  return errMsg;
}

function getHardwareErrorForKCC(errCode: string, defaultMsg: string) {
  const hwErrMap = new Map();

  // printer codes
  hwErrMap.set('printer_not_found', 'Printer Not Found');
  hwErrMap.set('printer_connect_error', 'Printer Connection Error');
  hwErrMap.set('paper_jam', 'Paper Jam');
  hwErrMap.set('out_of_stock', 'Out Of Stock');
  // payment device codes
  hwErrMap.set('device_error', 'Payment Device Error');
  // scanner codes
  hwErrMap.set('scanner_not_found', 'Scanner Not Found');

  return hwErrMap.get(errCode) || defaultMsg;
}

function* updateTransactionsWithPrintResults() {
  const { printJobs }: PrintData = yield select(hardwareSelectors.selectPrintData);

  const payload: UpdateTransactionsAsyncRequest['payload'] = printJobs.reduce(
    (acc: UpdateTransactionsAsyncRequest['payload'], printJob: PrintJob) => {
      const { transactionId, ticketNumber, printed } = printJob;
      // Exclude receipts and jobs that don't have a transactionId
      if (transactionId && ticketNumber !== 'receipt') {
        const accIndex = acc.findIndex((i) => i.transactionId === transactionId);
        if (accIndex === -1) {
          // Add an entry for the transactionId if it doesn't exist
          acc.push({
            transactionId,
            deliveryFailedTicketCount: printed ? null : 1
          });
        } else if (accIndex !== -1 && !printed) {
          // Increment the failed count for the transactionId for each job that failed
          acc[accIndex].deliveryFailedTicketCount = (acc[accIndex].deliveryFailedTicketCount || 0) + 1;
        }
      }
      return acc;
    },
    []
  );

  yield put(kioskActions.updateTransactions(payload));
}

export function* configurePrinterAsync({ payload }: ConfigurePrinterRequest) {
  const { kioskHardwareId } = payload;

  yield put({ type: types.CONFIGURE_PRINTER_ASYNC.PENDING, payload: { kioskHardwareId, pending: true } });

  try {
    const kioskPrinters: KioskHardware[] = yield select(kioskSelectors.selectPrinters);

    const kioskPrinter = kioskPrinters.find((p) => p.id === kioskHardwareId) as KioskHardware;
    const printerRoute = kioskPrinter && kioskPrinter.details && kioskPrinter.details.route;
    const stockHeight = kioskPrinter && kioskPrinter.configuration.stockHeight;

    // Only call the API if the route is boca and there is a stockHeight
    if (printerRoute === 'boca' && stockHeight) {
      const api = new HardwareAPI(`printer/${printerRoute}/configure/once`);
      const apiPayload: { ticket_height: number; serialCode?: string } = { ticket_height: stockHeight };

      // Only include serialCode if there are multiple boca printers
      if (kioskPrinters.filter((p) => p.details.route === 'boca').length > 1) {
        apiPayload.serialCode = kioskPrinter.serialCode;
      }

      yield call(api.create, apiPayload);
    }

    yield put({ type: types.CONFIGURE_PRINTER_ASYNC.SUCCESS, payload: { kioskHardwareId } });
  } catch (err) {
    yield put({
      type: types.CONFIGURE_PRINTER_ASYNC.ERROR,
      payload: {
        kioskHardwareId,
        message: getErrorMessage(err as AxiosError)
      }
    });
  } finally {
    yield put({ type: types.CONFIGURE_PRINTER_ASYNC.PENDING, payload: { kioskHardwareId, pending: false } });
  }
}

function* pingOnePrinterAsync(payload: PingOnePrinterPayload) {
  const { kioskHardwareId, userInitiated } = payload;

  yield put({ type: types.PING_ONE_PRINTER_ASYNC.PENDING, payload: { kioskHardwareId, pending: true } });

  const kioskPrinters: KioskHardware[] = yield select(kioskSelectors.selectPrinters);
  const kioskPrinter = kioskPrinters.find((printer) => printer.id === kioskHardwareId) as KioskHardware;
  const hardwarePrinters: Printer[] = yield select(hardwareSelectors.selectPrinters);
  const { status: printerStatus } = hardwarePrinters.find(
    (printer) => printer.kioskHardwareId === kioskHardwareId
  ) as Printer;

  try {
    const printerRoute = kioskPrinter.details.route || 'boca';
    const printerPingApi = new HardwareAPI(`printer/${printerRoute}/ping`);
    const queryParams: { serialCode?: string } = {};

    // Only include serialCode if the route is boca and there are multiple boca printers
    if (printerRoute === 'boca' && kioskPrinters.filter((p) => p.details.route === 'boca').length > 1) {
      queryParams.serialCode = kioskPrinter.serialCode;
    }

    const response: HardwareResponse = yield call(printerPingApi.getAll, queryParams);

    if (
      (printerStatus && [NOT_CONNECTED, ERROR].includes(printerStatus.code)) ||
      (kioskPrinter && kioskPrinter.status === null) ||
      (kioskPrinter && kioskPrinter.status === '') ||
      (!response.is_error && kioskPrinter.status === 'Inactive')
    ) {
      const eventPayload: EventPayload = {
        event: eventNames.PRINTER_RESOLVED,
        category: 'printer_status',
        kioskHardwareId,
        includeActor: userInitiated,
        status: 'Active'
      };
      yield put(eventActions.logEvent(eventPayload));
      yield put(kioskActions.updateHardware({ hardwareId: kioskHardwareId, status: 'Active' }));
    }

    yield put({
      type: types.PING_ONE_PRINTER_ASYNC.SUCCESS,
      payload: {
        kioskHardwareId,
        status: {
          code: CONNECTED,
          rawCode: response.code,
          message: response.message
        }
      }
    });
  } catch (err) {
    const statusCode = getErrorCode(err as AxiosError);
    const code = get(err, ['response', 'data', 'code'], 'unknown');

    let status: Status;
    if (statusCode === 404) {
      status = {
        code: NOT_CONNECTED,
        rawCode: code,
        message: yield* getHardwareError(code, (err as AxiosError).response?.data.message)
      };
    } else if (statusCode === 500) {
      status = {
        code: ERROR,
        rawCode: code,
        message: yield* getHardwareError(code, (err as AxiosError).response?.data.message)
      };
    } else {
      status = {
        code: ERROR,
        rawCode: 'unknown',
        message: 'Unknown'
      };
    }
    yield put({ type: types.PING_ONE_PRINTER_ASYNC.ERROR, payload: { kioskHardwareId, status } });

    if (status.rawCode === OUT_OF_STOCK) {
      yield put({ type: maintenanceTypes.SET_KIOSK_HARDWARE_ID_OUT_OF_STOCK, payload: kioskPrinter.id });
    }

    if (kioskPrinter && printerStatus && ![NOT_CONNECTED, ERROR].includes(printerStatus.code)) {
      const eventPayload: EventPayload = {
        event: getHardwareErrorForKCC(code, 'Unknown Printer Error'),
        category: 'printer_status',
        kioskHardwareId,
        includeActor: false,
        status: 'Inactive'
      };
      yield put(eventActions.logEvent(eventPayload));
      yield put(kioskActions.updateHardware({ hardwareId: kioskHardwareId, status: 'Inactive' }));
    }
  } finally {
    yield put({ type: types.PING_ONE_PRINTER_ASYNC.PENDING, payload: { kioskHardwareId, pending: false } });
  }
}

export function* pingPrintersAsync({ payload }: PingPrintersRequest) {
  const { kioskHardwareId, userInitiated } = payload;

  let hardwarePrinters: Printer[] = yield select(hardwareSelectors.selectPrinters);

  // -- Filter printers by kioskHardwareId if provided
  if (kioskHardwareId) {
    hardwarePrinters = hardwarePrinters.filter((p) => p.kioskHardwareId === kioskHardwareId);
  }

  for (const printer of hardwarePrinters) {
    yield call(pingOnePrinterAsync, {
      kioskHardwareId: printer.kioskHardwareId,
      userInitiated
    });
  }
}

function* printOneJobAsync(jobIndex: number) {
  const kioskPrinters: KioskHardware[] = yield select(kioskSelectors.selectPrinters);
  const { printJobs }: PrintData = yield select(hardwareSelectors.selectPrintData);
  const { kioskHardwareId, message } = printJobs[jobIndex];

  // If a hardwareId is provided, use that printer
  const kioskPrinter = kioskHardwareId
    ? (kioskPrinters.find((p) => p.id === kioskHardwareId) as KioskHardware)
    : kioskPrinters[0];

  try {
    // Delay the print if in preview mode
    if (sessionStorage.getItem('mode') === 'preview') yield delay(3000);

    const printerRoute = kioskPrinter.details.route || 'boca';
    const printerPrintApi = new HardwareAPI(`printer/${printerRoute}/print`);
    const apiPayload: { ticket: string; serialCode?: string } = { ticket: message as string };

    // Only include serialCode if the route is boca and there are multiple boca printers
    if (printerRoute === 'boca' && kioskPrinters.filter((p) => p.details.route === 'boca').length > 1) {
      apiPayload.serialCode = kioskPrinter.serialCode;
    }

    const response: { code: string; message: string } = yield call(printerPrintApi.create, apiPayload);

    // Update the printData and the printer status
    yield put({
      type: types.PRINT_ONE_JOB_ASYNC.SUCCESS,
      payload: {
        kioskHardwareId: kioskPrinter.id,
        status: {
          code: OPERATIONAL,
          rawCode: response.code,
          message: response.message
        },
        jobIndex
      }
    });

    return true;
  } catch (err) {
    const statusCode = getErrorCode(err as AxiosError);
    const code = get(err, ['response', 'data', 'code'], 'unknown');

    let status: Status;
    if (statusCode === 404) {
      status = {
        code: NOT_CONNECTED,
        rawCode: code,
        message: yield* getHardwareError(code, (err as AxiosError).response?.data.message)
      };
    } else if (statusCode === 500) {
      status = {
        code: ERROR,
        rawCode: code,
        message: yield* getHardwareError(code, (err as AxiosError).response?.data.message)
      };
    } else {
      status = {
        code: ERROR,
        rawCode: code,
        message: 'Unknown'
      };
    }
    yield put({ type: types.PRINT_ONE_JOB_ASYNC.ERROR, payload: { kioskHardwareId: kioskPrinter.id, status } });

    if (status.rawCode === OUT_OF_STOCK) {
      yield put({ type: maintenanceTypes.SET_KIOSK_HARDWARE_ID_OUT_OF_STOCK, payload: kioskPrinter.id });
    }

    const eventPayload: EventPayload = {
      event: getHardwareErrorForKCC(code, 'Unknown Printer Error'),
      category: 'printer_status',
      kioskHardwareId: kioskPrinter.id,
      status: 'Inactive',
      includeActor: false
    };
    yield put(eventActions.logEvent(eventPayload));
    yield put(kioskActions.setStatusActor('printerError'));
    yield put(kioskActions.updateHardware({ hardwareId: kioskPrinter.id, status: 'Inactive' }));

    return false;
  }
}

export function* printAllJobsAsync() {
  yield put({ type: types.PRINT_ALL_JOBS_ASYNC.PENDING, payload: true });

  const { printJobs, isReprint }: PrintData = yield select(hardwareSelectors.selectPrintData);

  let jobIndex = 0;
  let success = true;
  const failures = [];

  for (const printJob of printJobs) {
    if (!printJob.printed) {
      // If the previous prints were successful, keep printing
      if (success) success = yield call(printOneJobAsync, jobIndex);
      // After a failure, record the current and remaining prints as failed
      if (!success) failures.push(printJob);
    }
    jobIndex++;
  }

  // Update the transaction(s) if there are failures or if this is a reprint.
  // There is nothing to update if all prints were successful on a non-reprint.
  if (failures.length > 0 || isReprint) {
    yield call(updateTransactionsWithPrintResults);
  }

  yield put(ticketingActions.setDeliveryStatus(failures.length > 0 ? 'failed' : 'completed'));
  yield put(kioskActions.decrementStockCounts());
  yield put({ type: types.PRINT_ALL_JOBS_ASYNC.PENDING, payload: false });
}

export function* scannerPingAsync({ payload }: ScannerPingRequest) {
  yield put({ type: types.SCANNER_PING_ASYNC.PENDING, payload: true });
  const scannerHardware: KioskHardware = yield select(kioskSelectors.selectScannerHardware);
  const { status: scannerStatus }: Scanner = yield select(hardwareSelectors.selectScanner);

  try {
    const scannerApi = new HardwareAPI('scanner/ping');
    const response: HardwareResponse = yield call(scannerApi.getAll);

    if (
      (scannerStatus && [NOT_CONNECTED, ERROR].includes(scannerStatus.code)) ||
      (scannerHardware && scannerHardware.status === null) ||
      (scannerHardware && scannerHardware.status === '') ||
      (!response.is_error && scannerHardware.status === 'Inactive')
    ) {
      const eventPayload: EventPayload = {
        event: eventNames.SCANNER_RESOLVED,
        category: 'scanner_status',
        kioskHardwareId: scannerHardware.id,
        includeActor: payload.userInitiated,
        status: 'Active'
      };
      yield put(eventActions.logEvent(eventPayload));
      yield put(kioskActions.updateHardware({ hardwareId: scannerHardware.id, status: 'Active' }));
    }

    yield put({
      type: types.SCANNER_PING_ASYNC.SUCCESS,
      payload: {
        code: OPERATIONAL,
        rawCode: response.code
      }
    });
  } catch (err) {
    const status = getErrorCode(err as AxiosError);
    const code = get(err, ['response', 'data', 'code'], 'unknown');
    if (status === 404) {
      yield put({
        type: types.SCANNER_PING_ASYNC.ERROR,
        payload: {
          code: NOT_CONNECTED,
          rawCode: code,
          message: yield* getHardwareError(code, (err as AxiosError).response?.data.message)
        }
      });
    } else if (status === 500) {
      yield put({
        type: types.SCANNER_PING_ASYNC.ERROR,
        payload: {
          code: ERROR,
          rawCode: code,
          message: yield* getHardwareError(code, (err as AxiosError).response?.data.message)
        }
      });
    } else {
      yield put({
        type: types.SCANNER_PING_ASYNC.ERROR,
        payload: {
          code: ERROR,
          rawCode: 'unknown',
          message: 'Unknown'
        }
      });
    }
    if (scannerHardware && scannerStatus && ![NOT_CONNECTED, ERROR].includes(scannerStatus.code)) {
      const eventPayload: EventPayload = {
        event: getHardwareErrorForKCC(code, 'Unknown Scanner Error'),
        category: 'scanner_status',
        kioskHardwareId: scannerHardware.id,
        includeActor: false,
        status: 'Inactive'
      };
      yield put(eventActions.logEvent(eventPayload));
      yield put(kioskActions.updateHardware({ hardwareId: scannerHardware.id, status: 'Inactive' }));
    }
  } finally {
    yield put({ type: types.SCANNER_PING_ASYNC.PENDING, payload: false });
  }
}

export function* scannerReadAsync() {
  yield put({ type: types.SCANNER_READ_ASYNC.PENDING, payload: true });

  try {
    const scannerApi = new HardwareAPI('scanner/read');
    const response: { code: string; message: string } = yield call(scannerApi.getAll);

    yield put({
      type: types.SCANNER_READ_ASYNC.SUCCESS,
      payload: {
        code: OPERATIONAL,
        rawCode: response.code,
        message: response.message
      }
    });
  } catch (err) {
    const status = getErrorCode(err as AxiosError);
    if (status === 400) {
      yield put({
        type: types.SCANNER_READ_ASYNC.ERROR,
        payload: {
          code: USER_ERROR,
          rawCode: (err as AxiosError).response?.data.code,
          message: yield* getHardwareError(
            (err as AxiosError).response?.data.code,
            (err as AxiosError).response?.data.message
          )
        }
      });
    } else if (status === 404) {
      yield put({
        type: types.SCANNER_READ_ASYNC.ERROR,
        payload: {
          code: NOT_CONNECTED,
          rawCode: (err as AxiosError).response?.data.code,
          message: yield* getHardwareError(
            (err as AxiosError).response?.data.code,
            (err as AxiosError).response?.data.message
          )
        }
      });
    } else {
      yield put({
        type: types.SCANNER_READ_ASYNC.ERROR,
        payload: {
          code: ERROR,
          rawCode: (err as AxiosError).response?.data.code,
          message: yield* getHardwareError(
            (err as AxiosError).response?.data.code,
            (err as AxiosError).response?.data.message
          )
        }
      });
    }
  } finally {
    yield put({ type: types.SCANNER_READ_ASYNC.PENDING, payload: false });
  }
}

// PAYMENT PING
export function* paymentPingAsync({ payload }: PaymentPingRequest) {
  const { status: paymentStatus }: PaymentDevice = yield select(hardwareSelectors.selectPaymentDevice);
  const paymentHardware: KioskHardware = yield select(kioskSelectors.selectPaymentHardware);
  const details = paymentHardware.details as PaymentConfig;

  if (details.gateway === 'tms' || details.gateway === 'stripe') {
    yield put({
      type: types.PAYMENT_PING_ASYNC.SUCCESS,
      payload: {
        code: NO_PING,
        rawCode: 'no ping',
        message: 'no ping'
      }
    });
  } else {
    yield put({ type: types.PAYMENT_PING_ASYNC.PENDING, payload: true });

    try {
      let pingResponse: HardwareResponse;
      if (details.gateway === 'stripe_ticketure') {
        // connectionToken is only needed to initially connect to the device
        if (!stripeInstance.connectionToken) {
          const api = new API(`ticketing/connectionToken`);
          const response: { connectionToken: string } = yield call(api.create, {});
          stripeInstance.connectionToken = response.connectionToken;
        }
        // since the device status is checked through the app, log the response to kls
        try {
          pingResponse = yield stripeInstance.pingReader(details.configuration.readerId);
          yield put(hardwareActions.logToLocalServer(JSON.stringify(pingResponse)));
        } catch (err) {
          const responseData = get(err, 'response.data', err);
          yield put(hardwareActions.logToLocalServer(JSON.stringify(responseData)));
          throw err;
        }
      } else {
        // if triPOS_auth, use triPOS since the ping op is the same
        const gateway = details.gateway === 'triPOS_auth' ? 'triPOS' : details.gateway;
        const paymentApi = new HardwareAPI(`payment/${gateway}/ping`);
        pingResponse = yield call(paymentApi.getAll, details.configuration);
      }

      if (
        (paymentStatus && [NOT_CONNECTED, ERROR].includes(paymentStatus.code)) ||
        (paymentHardware && paymentHardware.status === null) ||
        (paymentHardware && paymentHardware.status === '') ||
        (!pingResponse.is_error && paymentHardware.status === 'Inactive')
      ) {
        const eventPayload: EventPayload = {
          event: eventNames.PAYMENT_RESOLVED,
          category: 'payment_status',
          kioskHardwareId: paymentHardware.id,
          includeActor: payload.userInitiated,
          status: 'Active'
        };
        yield put(eventActions.logEvent(eventPayload));
        yield put(kioskActions.updateHardware({ hardwareId: paymentHardware.id, status: 'Active' }));
      }

      yield put({
        type: types.PAYMENT_PING_ASYNC.SUCCESS,
        payload: {
          code: OPERATIONAL,
          rawCode: pingResponse.code,
          message: pingResponse.message
        }
      });
    } catch (err) {
      const status = getErrorCode(err as AxiosError);
      const code = get(err, ['response', 'data', 'code'], 'unknown');
      if (status === 404) {
        yield put({
          type: types.PAYMENT_PING_ASYNC.ERROR,
          payload: {
            code: NOT_CONNECTED,
            rawCode: code,
            message: yield* getHardwareError(code, (err as AxiosError).response?.data.message)
          }
        });
      } else if (status === 400 || status === 500) {
        yield put({
          type: types.PAYMENT_PING_ASYNC.ERROR,
          payload: {
            code: ERROR,
            rawCode: code,
            message: yield* getHardwareError(code, (err as AxiosError).response?.data.message)
          }
        });
      } else {
        yield put({
          type: types.PAYMENT_PING_ASYNC.ERROR,
          payload: {
            code: ERROR,
            rawCode: 'unknown',
            message: 'Unknown'
          }
        });
      }
      if (paymentHardware && paymentStatus && ![NOT_CONNECTED, ERROR].includes(paymentStatus.code)) {
        const eventPayload: EventPayload = {
          event: getHardwareErrorForKCC(code, 'Unknown Payment Device Error'),
          category: 'payment_status',
          kioskHardwareId: paymentHardware.id,
          includeActor: false,
          status: 'Inactive'
        };
        yield put(eventActions.logEvent(eventPayload));
        yield put(kioskActions.updateHardware({ hardwareId: paymentHardware.id, status: 'Inactive' }));
      }
    } finally {
      yield put({ type: types.PAYMENT_PING_ASYNC.PENDING, payload: false });
    }
  }
}

// PAYMENT SALE
export function* paymentSaleAsync() {
  yield put({ type: types.PAYMENT_SALE_ASYNC.PENDING, payload: true });
  const paymentHardware: KioskHardware = yield select(kioskSelectors.selectPaymentHardware);
  const { releaseParams, paymentIntentParams, authorizeParams }: Cart = yield select(cartSelectors.selectCart);
  const totalToCharge: number = yield select(cartSelectors.selectCartTotalToCharge);

  try {
    const details = paymentHardware.details as PaymentConfig;
    let response: HardwareResponse;

    if (details.gateway === 'tms' || details.gateway === 'stripe') {
      try {
        const session: string | null = yield select(ticketingSelectors.selectSession);
        const authorizePaymentApi = new API(`ticketing/payments/authorize?session=${session}`);
        const authorizePaymentResponse: AuthorizePaymentResponse = yield call(authorizePaymentApi.create, {
          amount: totalToCharge,
          paymentDeviceConfig: details.configuration,
          authorizeParams
        });
        response = {
          code: 'Success',
          message: authorizePaymentResponse
        };
      } catch (err) {
        const errorObj = {
          response: {
            status: 400,
            data: { code: 'unknown' }
          }
        };
        throw errorObj;
      }
    } else if (details.gateway === 'stripe_ticketure') {
      // connectionToken is only needed to initially connect to the device
      if (!stripeInstance.connectionToken) {
        const connectionTokenApi = new API('ticketing/connectionToken');
        const getTokenResponse: { connectionToken: string } = yield call(connectionTokenApi.create, {});
        stripeInstance.connectionToken = getTokenResponse.connectionToken;
      }

      // create the payment intent
      const paymentIntentApi = new API('ticketing/paymentIntent');
      const paymentIntentResponse: { clientSecret: string } = yield call(paymentIntentApi.create, {
        amount: totalToCharge,
        integrationParams: paymentIntentParams
      });

      // since the payment is processed through the FE, log the response to kls
      try {
        response = yield stripeInstance.collectAndProcessPayment(
          details.configuration.readerId,
          details.configuration.timeout,
          paymentIntentResponse.clientSecret
        );
        yield put(hardwareActions.logToLocalServer(JSON.stringify(response)));
      } catch (err) {
        const responseData = get(err, 'response.data', err);
        yield put(hardwareActions.logToLocalServer(JSON.stringify(responseData)));
        throw err;
      }
    } else if (details.gateway === 'triPOS_auth') {
      const paymentApi = new HardwareAPI(`payment/triPOS/auth`);
      response = yield call(paymentApi.create, {
        amount: totalToCharge.toString(),
        ...details.configuration,
        orderNumber: releaseParams && releaseParams.orderId && releaseParams.orderId.toString()
      });
    } else {
      const paymentApi = new HardwareAPI(`payment/${details.gateway}/sale`);
      response = yield call(paymentApi.create, {
        amount: totalToCharge.toString(),
        ...details.configuration,
        orderNumber: releaseParams && releaseParams.orderId && releaseParams.orderId.toString()
      });
    }

    yield put({
      type: types.PAYMENT_SALE_ASYNC.SUCCESS,
      payload: {
        status: {
          code: OPERATIONAL,
          rawCode: response.code,
          message: 'Transaction completed.'
        },
        saleData: response.message
      }
    });
  } catch (err) {
    const status = getErrorCode(err as AxiosError);
    const code = get(err, ['response', 'data', 'code'], 'unknown');
    if (status === 400) {
      yield put({
        type: types.PAYMENT_SALE_ASYNC.ERROR,
        payload: {
          code: USER_ERROR,
          rawCode: code,
          message: yield* getHardwareError(code, (err as AxiosError).response?.data.message)
        }
      });
    } else {
      yield put({
        type: types.PAYMENT_SALE_ASYNC.ERROR,
        payload: {
          code: ERROR
        }
      });
      const eventPayload: EventPayload = {
        event: getHardwareErrorForKCC(code, 'Unknown Payment Error'),
        category: 'payment_status',
        kioskHardwareId: paymentHardware.id,
        includeActor: false,
        status: 'Inactive'
      };
      yield put(eventActions.logEvent(eventPayload));
      yield put(kioskActions.setStatusActor('paymentDeviceError'));
      yield put(kioskActions.updateHardware({ hardwareId: paymentHardware.id, status: 'Inactive' }));
    }
  } finally {
    yield put({ type: types.PAYMENT_SALE_ASYNC.PENDING, payload: false });
  }
}

// PAYMENT VOID
export function* paymentVoidAsync({ payload }: PaymentVoidRequest) {
  yield put({ type: types.PAYMENT_VOID_ASYNC.PENDING, payload: true });

  try {
    const paymentHardware: KioskHardware = yield select(kioskSelectors.selectPaymentHardware);
    const session: string | null = yield select(ticketingSelectors.selectSession);

    const details = paymentHardware.details as PaymentConfig;

    if (details.gateway === 'tms' || details.gateway === 'stripe') {
      const paymentReverseAPI = new API(`ticketing/payments/reverse?session=${session}`);
      yield call(paymentReverseAPI.create, {
        voidParams: payload,
        paymentDeviceConfig: details.configuration
      });
    } else if (details.gateway === 'stripe_ticketure') {
      return;
    } else {
      let path = `payment/${details.gateway}/void`;
      if (details.gateway === 'triPOS_auth') {
        path = 'payment/triPOS/reverse';
      }
      const paymentApi = new HardwareAPI(path);
      yield call(paymentApi.create, {
        voidParams: payload,
        ...details.configuration
      });
    }

    yield put({ type: types.PAYMENT_VOID_ASYNC.SUCCESS });
  } catch (err) {
    const message = getErrorMessage(err as AxiosError) || i18n.t('sagaError.voidFailure');
    yield put({ type: types.PAYMENT_VOID_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.PAYMENT_VOID_ASYNC.PENDING, payload: false });
  }
}

// PAYMENT CANCEL
export function* paymentCancelAsync() {
  yield put({ type: types.PAYMENT_CANCEL_ASYNC.PENDING, payload: true });
  const paymentHardware: KioskHardware = yield select(kioskSelectors.selectPaymentHardware);

  try {
    const details = paymentHardware.details as PaymentConfig;

    if (details.gateway === 'stripe') {
      const paymentApi = new API(`ticketing/payments/cancel`);
      yield call(paymentApi.create, { paymentDeviceConfig: details.configuration });
    } else if (details.gateway === 'stripe_ticketure') {
      // since the payment is canceled through the FE, log the response to kls
      try {
        const response: HardwareResponse = yield stripeInstance.cancelCollectPayment(details.configuration.readerId);
        yield put(hardwareActions.logToLocalServer(JSON.stringify(response)));
      } catch (err) {
        const responseData = get(err, 'response.data', err);
        yield put(hardwareActions.logToLocalServer(JSON.stringify(responseData)));
        throw err;
      }
    } else {
      const paymentApi = new HardwareAPI(`payment/${details.gateway}/cancel`);
      yield call(paymentApi.create, details.configuration);
    }

    yield put({ type: types.PAYMENT_CANCEL_ASYNC.SUCCESS });
  } catch (err) {
    const message = getErrorMessage(err as AxiosError) || i18n.t('sagaError.cancelFailure');

    yield put({ type: types.PAYMENT_CANCEL_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.PAYMENT_CANCEL_ASYNC.PENDING, payload: false });
  }
}

export function* cameraSnapAsync() {
  yield put({ type: types.CAMERA_SNAP_ASYNC.PENDING, payload: true });
  try {
    const cameraSnapApi = new HardwareAPI('camera/picture');
    const response: { code: string; message: string } = yield call(cameraSnapApi.getAll);

    yield put({
      type: types.CAMERA_SNAP_ASYNC.SUCCESS,
      payload: {
        code: response.code,
        rawCode: response.code,
        message: response.message
      }
    });
  } catch (err) {
    const status = getErrorCode(err as AxiosError);
    if (status === 400) {
      yield put({
        type: types.CAMERA_SNAP_ASYNC.ERROR,
        payload: {
          code: UNKNOWN,
          rawCode: (err as AxiosError).response?.data.code,
          message: yield* getHardwareError(
            (err as AxiosError).response?.data.code,
            (err as AxiosError).response?.data.message
          )
        }
      });
    }
  }
}

// EVENT DATA PING
export function* eventDataPingAsync() {
  yield put({ type: types.EVENT_DATA_PING_ASYNC.PENDING, payload: true });

  try {
    const response: { apiVersion: string } = yield call(checkHealth);
    yield put({
      type: types.EVENT_DATA_PING_ASYNC.SUCCESS,
      payload: {
        code: OPERATIONAL,
        rawCode: response.apiVersion,
        message: 'API connected.'
      }
    });
  } catch (err) {
    yield put({
      type: types.EVENT_DATA_PING_ASYNC.ERROR,
      payload: {
        code: NOT_CONNECTED,
        rawCode: err,
        message: 'API not found.'
      }
    });
  } finally {
    yield put({ type: types.EVENT_DATA_PING_ASYNC.PENDING, payload: false });
  }
}

export function* logToLocalServer({ payload }: LogToLocalServer) {
  try {
    const api = new HardwareAPI(`log`);
    yield call(api.create, { message: payload });
  } catch (err) {
    // Do nothing
  }
}

export const sagas = [
  takeLatest(types.CONFIGURE_PRINTER_ASYNC.REQUEST, configurePrinterAsync),
  takeLatest(types.PING_PRINTERS_ASYNC.REQUEST, pingPrintersAsync),
  takeLeading(types.PRINT_ALL_JOBS_ASYNC.REQUEST, printAllJobsAsync),
  takeLatest(types.SCANNER_PING_ASYNC.REQUEST, scannerPingAsync),
  takeLatest(types.SCANNER_READ_ASYNC.REQUEST, scannerReadAsync),
  takeLatest(types.PAYMENT_PING_ASYNC.REQUEST, paymentPingAsync),
  takeLeading(types.PAYMENT_SALE_ASYNC.REQUEST, paymentSaleAsync),
  takeLeading(types.PAYMENT_VOID_ASYNC.REQUEST, paymentVoidAsync),
  takeLatest(types.PAYMENT_CANCEL_ASYNC.REQUEST, paymentCancelAsync),
  takeLatest(types.CAMERA_SNAP_ASYNC.REQUEST, cameraSnapAsync),
  takeLatest(types.EVENT_DATA_PING_ASYNC.REQUEST, eventDataPingAsync),
  takeLatest(types.LOG_TO_LOCAL_SERVER, logToLocalServer)
];
